/**
 * entry-card.tsx — the way in's card and its parts. The card is a tile's
 * surface where a section's page puts its night board, beside the page
 * header's words (entry-frame.tsx): who is joining, how the join stands, what
 * refused it, and the one gold action — the last three one component,
 * JoinStatus, for every way in that joins. A card that says one thing — the
 * landing's invitation, the session's end — leads with a head: a round mark
 * in its tone beside the words.
 */

import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ConnectionStep } from '../../domains/auth';
import type { RefusalWords } from '../../domains/join-refusals';
import { Button } from '../ui/button';
import { GLYPH, type Drawing } from '../ui/glyphs';
import { Notice } from '../ui/notice';
import { SegmentBar } from '../ui/segment-bar';
import { Spinner } from '../ui/spinner';
import { StatusLine } from '../ui/status-line';
import { StatementRow } from '../ui/tile';
import { ToneMark } from '../ui/tone-mark';
import utilities from '../../styles/utilities.module.css';
import styles from './entry-card.module.css';
import { joinParts, partLine, stepOf, type StatusPart } from './join-lines';
import { STEP_WORDS, STEPS } from './join-steps';
import type { JoinState } from './use-join-state';

/** The way in's card: its parts 16px apart on a tile's surface. */
export function EntryCard({ children }: { children: ReactNode }) {
  return <section className={styles.card}>{children}</section>;
}

interface EntryHeadProps {
  /** The drawing in the round mark, at a notice's size (ToneMark lg), in its tone. */
  glyph: Drawing;
  /** The accent for the player's own moment, the soft ink when it only says. */
  tone: 'accent' | 'neutral';
  /** A title, when the card has one: the landing's invitation, a match left. */
  title?: ReactNode;
  /** The sentence beside the mark, or under the title. */
  children?: ReactNode;
}

/** A card's head: the round mark in its tone beside a title and a sentence, as a notice sets them. */
export function EntryHead({ glyph, tone, title, children }: EntryHeadProps) {
  return (
    <div className={utilities.besideMark}>
      <ToneMark tone={tone} size="lg" glyph={glyph} />
      <div className={utilities.besideMarkWords}>
        {title != null && <h2 className={styles.headTitle}>{title}</h2>}
        {children != null && <p className={styles.headSentence}>{children}</p>}
      </div>
    </div>
  );
}

/**
 * Who is joining: what they are (Player, Moderator) over their own name in
 * the accent — the one thing on a way in that is "you", before there is a
 * team — and after a moderator's name the chip the rail gives a moderator.
 */
export function EntryWho({ label, name, chip }: { label: string; name: string; chip?: ReactNode }) {
  return (
    <div className={styles.who}>
      <span className={utilities.captionLabel}>{label}</span>
      <span className={styles.name}>
        {name}
        {chip != null && <span className={styles.chip}>{chip}</span>}
      </span>
    </div>
  );
}

/** What the link says of the join besides the name — the class — as a panel's rows. */
export function EntryFacts({ facts }: { facts: readonly { label: string; value: string }[] }) {
  return (
    <div className={styles.facts}>
      {facts.map(({ label, value }) => (
        <StatementRow key={label} size="sm" label={label} value={value} />
      ))}
    </div>
  );
}

/**
 * The join under way: the spinner and the step in words, how far it has come
 * ("3 of 4"), and the four steps in the gold of progress, in a well. The
 * caller keeps it in the card's status (EntryStatus), whose line says each
 * step (partLine).
 */
function JoinProgress({ step }: { step: ConnectionStep }) {
  const { t } = useTranslation();
  const n = STEPS.indexOf(step) + 1;
  const of = stepOf(step, t);
  return (
    <div className={styles.progress}>
      <p className={styles.stepLine}>
        <Spinner />
        <span>{t(STEP_WORDS[step])}</span>
        <span className={styles.count}>{of}</span>
      </p>
      <SegmentBar value={n} steps={STEPS.length} tone="progress" size="sm" stretch label={of} />
    </div>
  );
}

/** A wait with no steps — a link being checked, a session being waited for: the spinner and what is under way. */
function JoinWait({ children }: { children: ReactNode }) {
  return (
    <div className={styles.progress}>
      <p className={styles.stepLine}>
        <Spinner />
        <span>{children}</span>
      </p>
    </div>
  );
}

/**
 * A link's missing parameters, by the names the link spells them with, in
 * the label face, after the sentence that says they are missing: what an
 * incomplete staff link needs, said whole.
 */
export function MissingParams({ names }: { names: readonly string[] }) {
  const { t } = useTranslation();
  return (
    <>
      {t('entry.missingParams', { count: names.length })}{' '}
      {names.map((name, i) => (
        <Fragment key={name}>
          {i > 0 && ', '}
          <code className={styles.param}>{name}</code>
        </Fragment>
      ))}
    </>
  );
}

/**
 * A refusal on the card: the Notice in its tone, its title and the line under
 * it — the words a refusal's look gives (domains/join-refusals.ts), or, where
 * the card has more to say than words (a link's missing parts), `detail`.
 */
export function RefusalNotice({ words, detail }: { words: RefusalWords; detail?: ReactNode }) {
  return <Notice tone={words.tone} glyph={GLYPH.warning} title={words.title} detail={detail ?? words.detail} />;
}

/**
 * The card's part that changes as the join goes — its steps, a wait, a
 * refusal — drawn for the eye as it comes, and said by a status line beside
 * it, both from one list of parts (join-lines.ts): each drawn by its kind here
 * (drawPart) and said by its kind there (partLine). A live region holding it
 * would not be heard as the card arrives with it, and one holding it back
 * until it would be heard would leave the card to grow, and move, as it first
 * showed it: the card is centred, and the words beside it with it. The status
 * line holds its first words back, then says each change at once. What is
 * drawn is hidden from a screen reader, so nothing in it takes focus or stands
 * in the outline: a match left's head is drawn beside it, in the outline, in
 * the words the line says it with.
 */
export function EntryStatus({ parts }: { parts: readonly StatusPart[] }) {
  const { t } = useTranslation();
  const head = parts.find((part) => part.kind === 'left');
  return (
    <>
      {head && <EntryHead glyph={GLYPH.disconnect} tone="neutral" title={partLine(head, t)} />}
      <div className={styles.changes} aria-hidden="true">
        {parts.map((part, i) => (
          <Fragment key={i}>{drawPart(part)}</Fragment>
        ))}
      </div>
      <StatusLine>{parts.map((part) => partLine(part, t)).join(' ')}</StatusLine>
    </>
  );
}

/**
 * A part as the card draws it, in the words its line says (partLine); a match
 * left's head stands beside the drawing. Every kind is drawn: one with no case
 * here is a type error, as one with no words is in partLine.
 */
function drawPart(part: StatusPart): ReactNode {
  switch (part.kind) {
    case 'left':
      return null;
    case 'hint':
      return <EntryCaption tone="soft">{part.words}</EntryCaption>;
    case 'wait':
      return <JoinWait>{part.words}</JoinWait>;
    case 'step':
      return <JoinProgress step={part.step} />;
    case 'refusal':
      return <RefusalNotice words={part.words} />;
    default:
      return part satisfies never;
  }
}

/** A caption's ink: quiet at the card's foot, soft where it says what the press does. */
const CAPTION = {
  quiet: utilities.captionQuiet,
  soft: utilities.captionLine,
} as const;

/**
 * A line of prose on the card: quiet at its foot — whom to ask if it seems a
 * mistake, what is left to do — or soft where it says what the press does.
 */
export function EntryCaption({ tone = 'quiet', children }: { tone?: keyof typeof CAPTION; children: ReactNode }) {
  return <p className={CAPTION[tone]}>{children}</p>;
}

/**
 * Whom a refused card says to ask: a player's administrator, where the
 * refusal says a mistake is theirs to mend, or — for staff, every time — the
 * portal, which opens the session again.
 */
const ASK = {
  admin: 'entry.contactAdmin',
  portal: 'entry.refusal.reopenFromPortal',
} as const;

/**
 * The card's part that follows the join, and its one gold action, from how
 * the join stands (use-join-state.ts): what its status shows and says — a
 * line before the press, the link being checked, the join's steps, the wait
 * for a session not up yet, a refusal, a match the player left — then the gold that
 * joins, retries or rejoins, where the state has one, focused as it comes,
 * and the caption: whom to ask about a refusal, or that the tab can close.
 */
export function JoinStatus({ state, onJoin, ask }: { state: JoinState; onJoin: () => void; ask: keyof typeof ASK }) {
  const { t } = useTranslation();
  const refused = state.kind === 'refused' ? state.words : null;
  const left = state.kind === 'left';
  const gold = state.kind === 'ready' ? state.label : left ? t('entry.rejoin') : refused?.retry ? t('entry.retry') : null;
  const caption = left ? t('entry.closeTab') : refused && (ask === 'portal' || refused.contact) ? t(ASK[ask]) : null;
  return (
    <>
      <EntryStatus parts={joinParts(state, t)} />
      {gold && (
        <Button tone="primary" block autoFocus onClick={onJoin}>
          {gold}
        </Button>
      )}
      {caption && <EntryCaption>{caption}</EntryCaption>}
    </>
  );
}
